// src\themes\default\components\PasswordStepLoginForm.tsx
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useRouter } from 'next/navigation';

import PasswordInput from '@/components/account/input/PasswordInput';
import { loadThemeComponent } from "@/core/config/theme-component-loader";
const CustomButton = loadThemeComponent("CustomButton")
import { useAuth } from '@/contexts/authContext';
import { useCartStore } from '@/core/hooks/cart/useCartStore';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { loginWithPassword } from '@/core/lib/api/main/auth';
import {
  getPasswordLoginSchema,
  PasswordLoginData,
} from '@/core/schemas/authSchema';
import { PasswordLoginStepProps } from '@/core/types/auth';

// اضافه کردن props برای setStep و goBack
interface ExtendedPasswordLoginStepProps extends PasswordLoginStepProps {
  setStep: (step: 1 | 2 | 3) => void;

  isLoading?: boolean;
}

export default function PasswordStepLoginForm({
  handleResend,
  mobile,
  session_token,
  setStep,
  isLoading,
}: ExtendedPasswordLoginStepProps) {
  const router = useRouter();
  const { setAuth } = useAuth();
  const { dictionary } = useDictionary();
  const passwordSchema = getPasswordLoginSchema(dictionary);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PasswordLoginData>({
    resolver: zodResolver(passwordSchema),
  });

  const handlePasswordLogin = async (data: PasswordLoginData) => {
    try {
      const result = await loginWithPassword({
        mobile,
        password: data.password,
        session_token,
      });

      if (result?.status === 'success') {
        setAuth(result.data);
        toast.success(result.message);
        router.push('/dashboard');

        // سینک کردن سبد خرید دیگه لازم نیست جلوی رفتن به داشبورد رو بگیره
        useCartStore.getState().syncCart(true)
          .then(() => useCartStore.getState().fetchCartFromBackend())
          .catch((err) => console.error('Cart sync error:', err));

      } else {
        setError('password', { type: 'server', message: result.message });
      }
    } catch (error) {
      console.error('Password login error:', error);
      toast.error(dictionary.common.error);
    }
  };

  const handleOtpLogin = async () => {
    try {
      await handleResend();
      setStep(3);
    } catch (error) {
      console.error('Failed to resend OTP code:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(handlePasswordLogin)} className="w-full">
      <div className="flex flex-col w-full mb-8 h-24">
        <PasswordInput
          placeholder=""
          errors={errors}
          name="password"
          register={register}
          disabled={isSubmitting || isLoading}
          label={dictionary.forms?.password}
          variant="secondary"
        />
      </div>

      <CustomButton
        type="submit"
        disabled={isSubmitting || isLoading}
        loading={isSubmitting}
        size="full"
      >
        {dictionary.forms?.login}
      </CustomButton>

      <button
        type="button"
        onClick={handleOtpLogin}
        className="cursor-pointer mt-2"
      >
        {dictionary.forms?.otpLogin}
      </button>
    </form>
  );
}